import { useState, useEffect } from 'react'
import { RedocStandalone } from 'redoc'
import { Loader2, RefreshCw, FileQuestion, PanelLeftClose, PanelLeftOpen, BookText } from 'lucide-react'
import { Button } from '@/components/ui/button'

const REDOC_OPTIONS = {
  scrollYOffset: 0,
  hideDownloadButton: false,
  theme: {
    colors: {
      primary: { main: '#42ade2' },
      text: { primary: '#0f172a', secondary: '#64748b' },
      border: { dark: '#e2e8f0', light: '#f1f5f9' },
    },
    typography: {
      fontFamily: 'inherit',
      fontSize: '14px',
      lineHeight: '1.6',
      headings: {
        fontFamily: 'inherit',
        fontWeight: '600',
      },
      code: {
        fontSize: '13px',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        backgroundColor: '#f1f5f9',
        color: '#0f172a',
        wrap: true,
      },
      links: { color: '#42ade2' },
    },
    sidebar: {
      width: '272px',
      backgroundColor: '#ffffff',
      textColor: '#334155',
      groupItems: {
        textTransform: 'none',
      },
      level1Items: {
        textTransform: 'none',
      },
    },
    rightPanel: {
      backgroundColor: '#0f172a',
      width: '40%',
    },
    codeBlock: {
      backgroundColor: '#1e293b',
    },
    shape: {
      borderRadius: '6px',
    },
    spacing: {
      unit: 5,
      sectionVertical: 24,
      sectionHorizontal: 40,
    },
  },
} as const

interface SpecInfo {
  title: string
  version: string
  description?: string
}

interface ApiDocsViewerProps {
  specUrl?: string
  spec?: object | null
  loading?: boolean
  error?: string | null
  notFound?: boolean
  onReload?: () => void
  title?: string
  logo?: string
}

export function ApiDocsViewer({
  specUrl,
  spec: specProp,
  loading: loadingProp,
  error: errorProp,
  notFound: notFoundProp,
  onReload,
  title = 'API Reference',
  logo,
}: ApiDocsViewerProps) {
  const externallyControlled = specProp !== undefined || loadingProp !== undefined || errorProp !== undefined || notFoundProp !== undefined
  const [internalSpec, setInternalSpec] = useState<object | null>(null)
  const [specInfo, setSpecInfo] = useState<SpecInfo | null>(null)
  const [internalLoading, setInternalLoading] = useState(true)
  const [internalError, setInternalError] = useState<string | null>(null)
  const [internalNotFound, setInternalNotFound] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  const spec = externallyControlled ? (specProp ?? null) : internalSpec
  const loading = externallyControlled ? !!loadingProp : internalLoading
  const error = externallyControlled ? (errorProp ?? null) : internalError
  const notFound = externallyControlled ? !!notFoundProp : internalNotFound

  async function loadSpec() {
    if (externallyControlled) {
      onReload?.()
      return
    }
    if (!specUrl) return
    setInternalLoading(true)
    setInternalError(null)
    setInternalSpec(null)
    setSpecInfo(null)
    setInternalNotFound(false)
    try {
      const res = await fetch(specUrl)
      if (res.status === 404) {
        setInternalNotFound(true)
        return
      }
      if (!res.ok) throw new Error(`Failed to load spec (${res.status})`)
      const contentType = res.headers.get('content-type') ?? ''
      if (!contentType.includes('json')) {
        setInternalNotFound(true)
        return
      }
      const data = await res.json()
      setInternalSpec(data)
      if (data?.info) {
        setSpecInfo({
          title: data.info.title || title,
          version: data.info.version || '',
          description: data.info.description,
        })
      }
    } catch (err) {
      setInternalError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setInternalLoading(false)
    }
  }

  useEffect(() => {
    if (externallyControlled) return
    loadSpec()
  }, [specUrl, externallyControlled])

  useEffect(() => {
    if (!externallyControlled) return
    const info = (spec as any)?.info
    if (info) {
      setSpecInfo({
        title: info.title || title,
        version: info.version || '',
        description: info.description,
      })
    } else {
      setSpecInfo(null)
    }
  }, [spec, externallyControlled, title])

  const displayTitle = specInfo?.title ?? title

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="shrink-0 flex items-center gap-3 px-5 h-14 border-b bg-slate-700 text-white">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white shrink-0">
            {logo
              ? <img src={logo} alt="" className="h-6 w-6 object-contain" />
              : <BookText className="h-5 w-5 text-slate-700" />}
          </div>
          <span className="text-base font-semibold truncate">
            {displayTitle}{specInfo?.version ? ` (${specInfo.version})` : ''}
          </span>
          {specInfo?.description && (
            <span className="hidden lg:block text-xs text-slate-300 truncate">
              — {specInfo.description}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {spec && !loading && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarCollapsed(v => !v)}
              className="size-8 text-slate-300 hover:text-white hover:bg-white/10"
              title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
            >
              {sidebarCollapsed
                ? <PanelLeftOpen className="size-4" />
                : <PanelLeftClose className="size-4" />
              }
            </Button>
          )}
          {!loading && !error && !notFound && (
            <Button
              variant="ghost"
              size="icon"
              onClick={loadSpec}
              className="size-8 text-slate-300 hover:text-white hover:bg-white/10"
              title="Reload spec"
            >
              <RefreshCw className="size-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
            <span className="text-sm">Loading API reference…</span>
          </div>
        )}

        {notFound && (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <FileQuestion className="size-10 text-muted-foreground/40" />
            <div className="text-center space-y-1">
              <p className="text-sm font-medium text-foreground">Spec not available yet</p>
              <p className="text-xs text-muted-foreground">
                The OpenAPI spec file has not been published for this API.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <div className="text-center space-y-1">
              <p className="text-sm font-medium">Failed to load spec</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={loadSpec}>
              Try again
            </Button>
          </div>
        )}

        {spec && !loading && (
          <div data-sidebar-collapsed={sidebarCollapsed} className="api-docs-redoc">
            <style dangerouslySetInnerHTML={{ __html: `
              [data-sidebar-collapsed="true"] > div > div:first-child {
                width: 0 !important;
                min-width: 0 !important;
                overflow: hidden !important;
                flex-shrink: 0;
              }
              [data-sidebar-collapsed="true"] > div > div:nth-child(2) {
                margin-left: 0 !important;
                padding-left: 0 !important;
              }
              /* Blue header treatment for the Redoc API title (e.g. "ConEx API Gateway (0.0.1)") */
              .api-docs-redoc .api-info h1 {
                background-color: #334155;
                color: #ffffff !important;
                padding: 0.75rem 1rem;
                border-radius: 0.5rem;
              }
              .api-docs-redoc .api-info h1 * {
                color: #ffffff !important;
              }
            `}} />
            <RedocStandalone spec={spec} options={REDOC_OPTIONS} />
          </div>
        )}
      </div>
    </div>
  )
}
